import { Button } from '@/components/ui/button';
import { FileUpload } from '@/components/ui/file-upload';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useCreateDeviceModel, useUpdateDeviceModel } from '@/hooks/useCatalogActions';
import type { DeviceModel } from '@octave/contracts';
import { createLogger } from '@/lib/logger';

const log = createLogger('DeviceModelForm');
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const deviceModelSchema = z.object({
  category: z.string().min(1, 'La catégorie est requise'),
  brand: z.string().min(1, 'La marque est requise'),
  name: z.string().min(1, 'Le nom est requis'),
  description: z.string().optional(),
  imageUrl: z.string().url('URL invalide').optional().or(z.literal('')),
  isActive: z.boolean().default(true),
});

type DeviceModelFormData = z.infer<typeof deviceModelSchema>;

interface DeviceModelFormProps {
  model?: DeviceModel | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const DEVICE_CATEGORIES = ['Smartphone', 'Tablette', 'Clé 4G'];

export function DeviceModelForm({
  model,
  onSuccess,
  onCancel,
}: DeviceModelFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createDeviceModel = useCreateDeviceModel();
  const updateDeviceModel = useUpdateDeviceModel();

  const form = useForm<DeviceModelFormData>({
    resolver: zodResolver(deviceModelSchema),
    defaultValues: {
      category: model?.category || '',
      brand: model?.brand || '',
      name: model?.name || '',
      description: model?.description || '',
      imageUrl: model?.imageUrl || '',
      isActive: model?.isActive ?? true,
    },
  });

  const onSubmit = async (data: DeviceModelFormData) => {
    setIsSubmitting(true);
    try {
      if (model) {
        updateDeviceModel.mutate({
          id: model.id,
          data,
        });
      } else {
        createDeviceModel.mutate(data);
      }
      onSuccess();
    } catch (error) {
      log.error('Form submission error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Settings */}
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Catégorie</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une catégorie" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DEVICE_CATEGORIES.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="brand"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Marque</FormLabel>
                    <FormControl>
                      <Input placeholder="Apple, Samsung, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom du modèle</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="iPhone 15, Galaxy S24, etc."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Description du modèle..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Actif</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Ce modèle sera visible dans le catalogue
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {/* Right Column - Image Upload */}
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="imageUrl"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FileUpload
                      value={field.value}
                      onChange={field.onChange}
                      label="Image du modèle"
                      placeholder="Télécharger une image"
                      category="devices"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              createDeviceModel.isPending ||
              updateDeviceModel.isPending
            }
          >
            {isSubmitting ||
            createDeviceModel.isPending ||
            updateDeviceModel.isPending
              ? 'Enregistrement...'
              : model
              ? 'Mettre à jour'
              : 'Créer'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
